// === ./src/frontend/components/FAQSection.jsx ===
import React, { useState, useContext } from "react";
import { Box, Container, Typography, Stack, Chip, alpha } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { slideInUp } from "./animations";
import { TranslateContext } from "../../context/TranslateContext";

const FAQSection = ({ theme }) => {
  const [open, setOpen] = useState(null);
  const { lang } = useContext(TranslateContext);

  const faqs = [
    {
      q: lang?.faqQ1 || "What types of files can I download?",
      a:
        lang?.faqA1 ||
        "TorrentCloud supports any file type available via magnet links — software ISOs, open-source archives, public domain media, academic datasets, and more. You are responsible for ensuring you have the right to download any content.",
    },
    {
      q: lang?.faqQ2 || "Is my IP address exposed to other peers?",
      a:
        lang?.faqA2 ||
        "No. Our servers act as the torrent client on your behalf. Your personal IP address is never visible to the torrent swarm. All communication between you and our servers is SSL encrypted.",
    },
    {
      q: lang?.faqQ3 || "How long are files kept in my cloud storage?",
      a:
        lang?.faqA3 ||
        "Files remain in your cloud storage as long as your account is active and within your storage quota. You can delete files at any time to free up space.",
    },
    {
      q: lang?.faqQ4 || "Can I access my files from multiple devices?",
      a:
        lang?.faqA4 ||
        "Yes. Your cloud storage is accessible from any browser on any device. Simply log in and your files are there — desktop, tablet, or mobile.",
    },
    {
      q: lang?.faqQ5 || "What storage providers are supported?",
      a:
        lang?.faqA5 ||
        "We support Local Storage, BunnyCDN, and AWS S3. Admins can configure the default provider. Pro and Enterprise plans get access to all provider options.",
    },
    {
      q: lang?.faqQ6 || "Is there a limit on torrent file size?",
      a:
        lang?.faqA6 ||
        "There is no hard limit on individual torrent size — it depends on your plan's total storage quota. Pro users get 100 GB and Enterprise users get 1 TB of cloud storage.",
    },
  ];

  return (
    <Box
      id="faq"
      sx={{
        py: { xs: 10, md: 14 },
        bgcolor: alpha(theme.palette.background.paper, 0.4),
      }}
    >
      <Container maxWidth="md">
        <Box textAlign="center" mb={8}>
          <Chip
            label={lang?.faqChip || "Got Questions?"}
            size="small"
            sx={{
              mb: 2,
              fontWeight: 700,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: "primary.main",
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          />
          <Typography
            variant="h2"
            fontWeight={800}
            sx={{
              fontSize: { xs: "2rem", md: "2.8rem" },
              letterSpacing: "-0.03em",
              mb: 2,
            }}
          >
            {lang?.faqTitle || "Frequently Asked Questions"}
          </Typography>
        </Box>

        <Stack spacing={1.5}>
          {faqs.map((faq, i) => (
            <Box
              key={i}
              sx={{
                borderRadius: 2.5,
                overflow: "hidden",
                border: `1px solid ${open === i ? alpha(theme.palette.primary.main, 0.3) : theme.palette.divider}`,
                transition: "all 0.25s ease",
                bgcolor:
                  open === i
                    ? alpha(theme.palette.primary.main, 0.03)
                    : "background.paper",
                animation: `${slideInUp} 0.5s ease both`,
                animationDelay: `${i * 0.07}s`,
              }}
            >
              <Box
                onClick={() => setOpen(open === i ? null : i)}
                sx={{
                  p: 2.5,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                  userSelect: "none",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                  },
                }}
              >
                <Typography variant="body1" fontWeight={600} sx={{ pr: 2 }}>
                  {faq.q}
                </Typography>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    flexShrink: 0,
                    bgcolor:
                      open === i
                        ? theme.palette.primary.main
                        : alpha(theme.palette.primary.main, 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.25s ease",
                  }}
                >
                  <ExpandMore
                    sx={{
                      fontSize: 18,
                      color: open === i ? "#fff" : "primary.main",
                      transform: open === i ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.25s ease",
                    }}
                  />
                </Box>
              </Box>
              {open === i && (
                <Box
                  sx={{
                    px: 2.5,
                    pb: 2.5,
                    animation: `${slideInUp} 0.2s ease`,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.75 }}
                  >
                    {faq.a}
                  </Typography>
                </Box>
              )}
            </Box>
          ))}
        </Stack>
      </Container>
    </Box>
  );
};

export default FAQSection;
